import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already dismissed or already installed
    const dismissed = localStorage.getItem('motoveloz-install-dismissed');
    if (dismissed) return;

    // Check if running as standalone (already installed)
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Fallback: show custom prompt after 3 seconds for browsers that don't fire beforeinstallprompt
    const timer = setTimeout(() => {
      if (!deferredPrompt) {
        const isAndroid = /android/i.test(navigator.userAgent);
        const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
        if (isAndroid || isIOS) {
          setShow(true);
        }
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShow(false);
        localStorage.setItem('motoveloz-install-dismissed', 'installed');
      }
      setDeferredPrompt(null);
    } else {
      // iOS or fallback - show instructions
      const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
      if (isIOS) {
        alert('Para instalar: toque no botão de compartilhar (⬆) e selecione "Adicionar à Tela de Início"');
      } else {
        alert('Para instalar: abra o menu do navegador (⋮) e selecione "Instalar aplicativo" ou "Adicionar à tela inicial"');
      }
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('motoveloz-install-dismissed', Date.now().toString());
  };

  if (!show) return null;

  return (
    <div data-testid="install-prompt" className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-[60] animate-slide-up">
      <div className="bg-zinc-900 border-2 border-red-600 p-4 shadow-2xl shadow-red-600/20 relative">
        <button onClick={handleDismiss} data-testid="install-dismiss-btn"
          className="absolute top-2 right-2 text-zinc-500 hover:text-white p-1 transition-colors">
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center">
            <img src="/icon-192.png" alt="MotoVeloz" className="w-10 h-10 rounded" />
          </div>
          <div className="flex-1 min-w-0">
            <p translate="no" className="text-white font-bold text-sm">
              <span>MOTO</span><span className="text-red-500">VELOZ</span>
            </p>
            <p className="text-zinc-400 text-xs">Instale para acesso rápido</p>
          </div>
        </div>
        <button onClick={handleInstall} data-testid="install-btn"
          className="w-full mt-3 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2">
          <Download className="h-4 w-4" /> Instalar Aplicativo
        </button>
      </div>
    </div>
  );
}
